import {
    inject,
    Injectable
} from "@angular/core";

import {
    EnvironmentInfoService
} from "src/app/core";

type OpenUrlInNewWindowOptions = {
    maximize?: boolean;
    electronOptions?: {
        width?: number;
        height?: number;
    };
};

@Injectable({
    providedIn: 'root'
})
export class SystemUtilsService {
    private readonly environmentInfo = inject(EnvironmentInfoService);

    /**
     * Opens the given url in an external browser, or in the default
     * system browser if the app is running in a web context.
     *
     * @param url the url to open
     */
    public openExternalLink(url: string) {
        if(this.environmentInfo.isElectronApp()) {
            window.require('electron').shell.openExternal(url);
        } else {
            window.open(url, '_system');
        }
    }

    /**
     * Opens the given URL in a new window.
     *
     * @param {string} url - The URL to open.
     * @param {OpenUrlInNewWindowOptions} [options] - Optional settings for the new window.
     * @return {void}
     */
    public openUrlInNewWindow(url: string, options?: OpenUrlInNewWindowOptions) {
        if(this.environmentInfo.isElectronApp()) {
            window.require('electron').ipcRenderer.invoke('open-url', { url, options });
        } else {
            window.open(url, '_blank');
        }
    }

    /**
     * Shares content using the system's native sharing mechanism.
     *
     * @param {Object} shareOptions - The options for sharing.
     * @param {string} [shareOptions.title] - The title of the message.
     * @param {string} [shareOptions.text] - The text content of the message.
     * @param {string} [shareOptions.url] - A URL to share.
     * @param {string[]} [shareOptions.files] - An array of files (as data URIs or paths) to share.
     * @return {Promise<void>} A promise that resolves when sharing is successful or rejects if unsupported.
     */
    public share(shareOptions: { title?: string, text?: string, url?: string, files?: string[] }) {
        if(!this.environmentInfo.canShare()) {
            return Promise.reject('UNSUPPORTED_PLATFORM');
        }

        return new Promise((resolve, reject) => {
            window.plugins.socialsharing.shareWithOptions({
                message: shareOptions.text,
                subject: shareOptions.title,
                files: shareOptions.files,
                url: shareOptions.url
            }, resolve, reject);
        });
    }

    /**
     * Minimizes the app.
     *
     * If the app is running in an electron context, sends an IPC
     * message to the main process to minimize the app.
     *
     * If the app is running in a web context, this function does
     * nothing.
     */
    public minimizeApp() {
        if(this.environmentInfo.isElectronApp()) {
            window.require('electron').ipcRenderer.send('electron-minimize-app');
        }
    }
}